import { ref, computed } from '@vue/composition-api'
import SdpPrinterHealthApi from '@/api/waiter/SdpPrinterHealth'
import useCurrentData from './useCurrentData'

// レイアウト共通で共有する singleton state(複数マウントでもポーリングは1本)
const stalePrinters = ref([])
const lastPolledAtByPrinter = ref({})
let timerId = null

const CHECK_INTERVAL_MS = 60000

/**
 * SDPプリンタの死活監視。
 *
 * プリンタ側SDPクライアントは電源投入時のネットワーク未確立などで
 * 起動失敗したまま沈黙することがある(2026-08-22実障害: 開店から夜まで
 * ポーリング不在で、顧客の「印刷できない」申告まで誰も気づけなかった)。
 * サーバの sdp_printer_health を定期取得し、途絶(stale)プリンタが
 * あればレイアウト共通バナーで店舗スタッフに知らせる。
 */
// 「42分前」「17時間前」のような相対表示。lastPolledAt が無ければ null
export const formatSince = (lastPolledAt, now = Date.now()) => {
  if (!lastPolledAt) return null
  const diffMs = now - new Date(lastPolledAt).getTime()
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 60) return `${Math.max(minutes, 1)}分前`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}時間前`

  return `${Math.floor(hours / 24)}日前`
}

// 「8/22 21:22」のような絶対時刻表示。当日なら時刻のみ。lastPolledAt が無ければ null
export const formatPolledAt = (lastPolledAt, now = new Date()) => {
  if (!lastPolledAt) return null
  const d = new Date(lastPolledAt)
  const hhmm = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()

  return sameDay ? hhmm : `${d.getMonth() + 1}/${d.getDate()} ${hhmm}`
}

export default function useSdpPrinterHealth() {
  const { currentClub } = useCurrentData()

  const fetchHealth = async () => {
    try {
      const res = await SdpPrinterHealthApi.getHealth()
      const printers = res.data?.printers || []
      stalePrinters.value = printers.filter(p => p.stale)
      lastPolledAtByPrinter.value = Object.fromEntries(printers.map(p => [p.id, p.lastPolledAt]))
    } catch {
      // 死活表示は付加機能のため、取得失敗時は表示を変えない(誤報を避ける)
    }
  }

  const startWatching = () => {
    // SDP を使わない店舗では監視しない
    if (!currentClub.value?.receiptSdpEnabled) return
    if (timerId) return

    fetchHealth()
    timerId = setInterval(fetchHealth, CHECK_INTERVAL_MS)
  }

  const stopWatching = () => {
    if (timerId) {
      clearInterval(timerId)
      timerId = null
    }
  }

  const hasStalePrinter = computed(() => stalePrinters.value.length > 0)

  const bannerMessage = computed(() => {
    if (!hasStalePrinter.value) return null
    const parts = stalePrinters.value.map(p => {
      const since = formatSince(p.lastPolledAt)
      const at = formatPolledAt(p.lastPolledAt)

      return since ? `${p.name}(最終ポーリング成功: ${at}・${since})` : `${p.name}(ポーリング記録なし)`
    })

    return `プリンタがサーバーと通信できていません: ${parts.join(' / ')}。プリンタの電源を入れ直してください`
  })

  return {
    stalePrinters,
    hasStalePrinter,
    bannerMessage,
    formatSince,
    lastPolledAtByPrinter,
    fetchHealth,
    startWatching,
    stopWatching,
  }
}
